import React, {useState, useEffect, useRef} from 'react';
import apiClient from '../../context/apiClient';
import {validators, autoFormat, validateEmployeeCounts} from '../../utils/validationHelpers';

const languageFlags = {
    DE: "🇩🇪", EN: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
    NE: "🇳🇱", PL: "🇵🇱", TR: "🇹🇷", PT: "🇵🇹", RU: "🇷🇺",
    AR: "🇸🇦", ZH: "🇨🇳", JA: "🇯🇵", KO: "🇰🇷", SE: "🇸🇪",
    NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", HU: "🇭🇺", CZ: "🇨🇿",
    SK: "🇸🇰", RO: "🇷🇴", BG: "🇧🇬", HR: "🇭🇷", EL: "🇬🇷",
};

const eventTypeLabels = {
    ADEC: "ADEC",
    GROS_DOLLN: "Groß Dölln",
    TOUREN: "Touren",
    ASCE: "ASCE",
    FINNLAND: "Finnland",
    SEEFELD: "Seefeld",
    SAALBACH: "Saalbach",
    ADE: "ADE",
    NICHT_EVENT: "Nicht-Event",
    ERROR: "Unbekannt"
};


const NewEventForm = ({onClose, onSuccess}) => {
    const [form, setForm] = useState({
        name: '',
        startTime: '',
        endTime: '',
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        country: '',
        numberLeader: 0,
        numberInstructors: 0,
        numberMechanics: 0,
        numberGuestManagers: 0,
        eventStatus: 'OPEN',
        eventType: '',
        leader: null,
        employeeList: [],
        qualifications: [],
        languages: [],
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});
    const [eventTypes, setEventTypes] = useState([]);
    const [qualifications, setQualifications] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [showQualificationModal, setShowQualificationModal] = useState(false);


    const [showLanguageSelect, setShowLanguageSelect] = useState(false);
    const languageDropdownRef = useRef(null);

    useEffect(() => {
        apiClient.get('/api/utility/allEventTypes').then(res => setEventTypes(res.data));
        apiClient.get('/api/utility/allQualifications').then(res => setQualifications(res.data.map(q => q.name)));
        apiClient.get('/api/utility/allLanguages').then(res => setLanguages(res.data));
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
                setShowLanguageSelect(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const isFormValid = Object.keys(validators).every((key) => {
        const val = validators[key](form[key], form);
        return val === true;
    });

    const handleChange = (key, value) => {
        const formatter = autoFormat[key] || autoFormat.default;
        const formatted = formatter(value);
        setForm((prev) => ({...prev, [key]: formatted}));

        if (formTouched[key]) validateField(key, formatted);
    };

    const handleBlur = (key) => {
        setFormTouched((prev) => ({...prev, [key]: true}));
        validateField(key, form[key]);
    };

    const validateField = (key, value) => {
        const result = validators[key](value, form);
        setFieldErrors((prev) => ({
            ...prev,
            [key]: result === true ? '' : result,
        }));
    };

    const handleAddLanguage = (lang) => {
        if (!form.languages.includes(lang)) {
            setForm(prev => ({...prev, languages: [...prev.languages, lang]}));
        }
    };

    const handleRemoveLanguage = (lang) => {
        setForm(prev => ({
            ...prev,
            languages: prev.languages.filter((l) => l !== lang)
        }));
    };

    const handleAddQualification = (q) => {
        if (!form.qualifications.includes(q)) {
            setForm(prev => ({...prev, qualifications: [...prev.qualifications, q]}));
        }
    };

    const handleRemoveQualification = (q) => {
        setForm(prev => ({
            ...prev,
            qualifications: prev.qualifications.filter(item => item !== q),
        }));
    };

    const handleSubmit = async () => {
        const newErrors = {};
        Object.keys(validators).forEach((key) => {
            const result = validators[key](form[key], form);
            if (result !== true) newErrors[key] = result;
        });

        const employeeError = validateEmployeeCounts(form);
        if (employeeError !== true) {
            newErrors.employeeCounts = employeeError;
        }

        if (!form.eventType) {
            newErrors.eventType = 'Bitte wählen Sie einen Eventtyp aus.';
        }


        if (Object.keys(newErrors).length > 0) {
            setFieldErrors(newErrors);
            return;
        }

        const locationString = `${form.street}, ${form.streetNumber}, ${form.postalCode}, ${form.city}, ${form.country}`;

        const payload = {
            name: form.name,
            timeframe: {
                startTime: form.startTime,
                endTime: form.endTime,
            },
            location: locationString,
            numberLeader: Math.max(0, Number(form.numberLeader)),
            numberInstructors: Math.max(0, Number(form.numberInstructors)),
            numberMechanics: Math.max(0, Number(form.numberMechanics)),
            numberGuestManagers: Math.max(0, Number(form.numberGuestManagers)),
            eventStatus: form.eventStatus,
            eventType: form.eventType,
            qualifications: form.qualifications.map((q) => ({name: q})),
            languages: form.languages,
        };

        try {
            console.log("Sending payload:", payload);
            const res = await apiClient.post('/api/events/addEvent', payload);
            if (res.status === 201 || res.status === 200) {
                onSuccess?.();
                onClose?.();
            } else {
                alert(`Fehler: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
            alert('Fehler beim Speichern des Events.');
        }
    };

    const renderInput = (label, key, type = 'text', colSpan = 'col-span-1') => (
        <div className={colSpan}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}*</label>
            <input
                type={type}
                value={form[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                min={type === 'number' ? 0 : undefined}
                onBlur={() => handleBlur(key)}
                className={`w-full border ${fieldErrors[key] ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2`}
            />
            {fieldErrors[key] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors[key]}</p>
            )}
        </div>
    );

    return (
        <div className="space-y-4 text-sm text-gray-800">
            {renderInput('Eventname', 'name')}
            <div>
                <label className="block mb-1">Eventtyp</label>
                <select
                    value={form.eventType || ''}
                    onChange={e => setForm(prev => ({...prev, eventType: e.target.value}))}
                    className="w-full border rounded px-2 py-1"
                >
                    <option value="">Bitte wählen</option>
                    {eventTypes.map(type => (
                        <option key={type} value={type}>
                            {eventTypeLabels[type] || type}
                        </option>
                    ))}
                </select>
                {fieldErrors.eventType && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.eventType}</p>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4">
                {renderInput('Startzeit', 'startTime', 'datetime-local')}
                {renderInput('Endzeit', 'endTime', 'datetime-local')}
            </div>
            <div className="grid grid-cols-2 gap-4">
                {renderInput('Straße', 'street')}
                {renderInput('Nr.', 'streetNumber')}
                {renderInput('PLZ', 'postalCode')}
                {renderInput('Stadt', 'city')}
                {renderInput('Land', 'country', 'text', 'col-span-2')}
            </div>

            {/* Qualifications */}
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualifikationen</label>
                <div className="flex flex-wrap gap-2 items-center mb-2">
                    {form.qualifications.map((q) => {
                        const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                        const base = match?.[1] || q;
                        const suffix = match?.[2] || 'DEFAULT';
                        const colorMap = {
                            KL: 'bg-indigo-500 text-white',
                            M: 'bg-emerald-200 text-emerald-900',
                            GM: 'bg-rose-200 text-rose-900',
                            DEFAULT: 'bg-indigo-100 text-indigo-800',
                        };

                        return (
                            <span
                                key={q}
                                className={`flex items-center text-xs px-3 py-1 rounded-full ${colorMap[suffix]}`}
                            >
                    {base}
                </span>
                        );
                    })}
                </div>
                <button
                    type="button"
                    onClick={() => setShowQualificationModal(true)}
                    className="text-sm text-indigo-600 underline hover:text-indigo-800"
                >
                    Qualifikationen bearbeiten
                </button>
                {showQualificationModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                        <div
                            className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Qualifikationen bearbeiten</h2>

                            <div className="flex flex-wrap gap-2 mb-4 max-h-[150px] overflow-y-auto pr-1">
                                {form.qualifications.map((q, idx) => {
                                    const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                    const base = match?.[1] || q;
                                    const suffix = match?.[2] || null;
                                    const colorMap = {
                                        KL: "bg-indigo-500 text-white",
                                        DEFAULT: "bg-indigo-100 text-indigo-800",
                                        M: "bg-emerald-200 text-emerald-900",
                                        GM: "bg-rose-200 text-rose-900",
                                    };

                                    return (
                                        <div
                                            key={idx}
                                            className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full ${colorMap[suffix] || colorMap.DEFAULT}`}
                                        >
                                            <span>{base}</span>
                                            <button
                                                onClick={() => handleRemoveQualification(q)}
                                                className="ml-1 text-xs font-bold hover:text-red-600 transition"
                                                title="Entfernen"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Dropdown only – no custom input */}
                            <select
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                onChange={(e) => {
                                    const selected = e.target.value;
                                    if (selected && !form.qualifications.includes(selected)) {
                                        handleAddQualification(selected);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">+ Qualifikation hinzufügen</option>
                                {qualifications.map((q) => (
                                    <option key={q} value={q}>{q}</option>
                                ))}
                            </select>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Languages */}
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sprachen</label>
                <div className="flex flex-wrap gap-2 items-center">
                    {form.languages.map(lang => (
                        <button
                            key={lang}
                            onClick={() => handleRemoveLanguage(lang)}
                            className="flex items-center space-x-1 bg-indigo-100 text-indigo-800 text-sm px-2 py-1 rounded-full hover:bg-red-100 hover:text-red-600 transition"
                            title="Klicken zum Entfernen"
                        >
                            <span>{languageFlags[lang] || lang}</span>
                            <span className="text-xs font-bold">&times;</span>
                        </button>
                    ))}
                    <div className="relative" ref={languageDropdownRef}>
                        <button
                            type="button"
                            onClick={() => setShowLanguageSelect(prev => !prev)}
                            className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center hover:bg-indigo-500 hover:text-white"
                            title="Sprache hinzufügen"
                        >
                            +
                        </button>
                        {showLanguageSelect && (
                            <div
                                className="absolute left-0 mt-2 z-20 bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-y-auto w-48">
                                {Object.keys(languageFlags)
                                    .filter((lang) => !form.languages.includes(lang))
                                    .map(lang => (
                                        <button
                                            key={lang}
                                            onClick={() => {
                                                handleAddLanguage(lang);
                                                setShowLanguageSelect(false);
                                            }}
                                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-indigo-100"
                                        >
                                            {languageFlags[lang]} {lang}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Kursleiter*</label>
                    <select
                        value={form.numberLeader}
                        onChange={e => setForm(prev => ({...prev, numberLeader: Number(e.target.value)}))}
                        className={`w-full border ${fieldErrors['numberLeader'] ? 'border-red-500' : 'border-gray-300'} rounded px-3 py-2`}
                    >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                    </select>
                    {fieldErrors['numberLeader'] && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors['numberLeader']}</p>
                    )}
                </div>
                {renderInput('Instruktoren', 'numberInstructors', 'number')}
                {renderInput('Mechaniker', 'numberMechanics', 'number')}
                {renderInput('Guestmanager', 'numberGuestManagers', 'number')}
            </div>
            {fieldErrors['employeeCounts'] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors['employeeCounts']}</p>
            )}

            <div className="flex justify-end gap-3 mt-4">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Abbrechen
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                    className={`px-4 py-2 text-sm rounded text-white ${isFormValid ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-400 cursor-not-allowed'}`}
                >
                    Speichern
                </button>
            </div>
        </div>
    );
};

export default NewEventForm;
